import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheckIcon } from 'lucide-react';
import { Logo } from '../../components/brand/Logo';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { usePortalAuth } from '../../context/PortalAuth';
export function PortalLogin() {
  const {
    user,
    signIn,
    completeMfa,
    pendingMfa
  } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [magicSent, setMagicSent] = React.useState(false);
  if (user) return <Navigate to="/portal/dashboard" replace data-id="element-1036" data-mp="2ir0rxhvsq2g6" />;
  const onSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await signIn(email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'We could not sign you in.');
      return;
    }
    if (!result.mfaRequired) navigate('/portal/dashboard');
  };
  const onMfa = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await completeMfa(code);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'That code is not valid.');
      return;
    }
    navigate('/portal/dashboard');
  };
  return <div className="flex min-h-screen w-full flex-col bg-white lg:flex-row" data-id="element-1037" data-mp="3rm4p7xl1tskn">
      <div className="surface-dark flex flex-col justify-between px-6 py-10 sm:px-12 lg:w-1/2 lg:py-16" data-id="element-1038" data-mp="3dog0genv21ab">
        <Link to="/" aria-label="Cavalla Solution, home" data-id="element-1039" data-mp="3ilolqo4b2lfs">
          <Logo variant="reversed" data-id="element-1040" data-mp="228hnhzpkav3t" />
        </Link>
        <div className="mt-16 max-w-md lg:mt-0" data-id="element-1041" data-mp="227opbn4o5dxa">
          <h1 className="font-display text-[32px] leading-tight text-white sm:text-[40px]" data-id="element-1042" data-mp="277kv9rwxfffh">
            Your work with us, in one place
          </h1>
          <p className="mt-5 text-[17px] text-white/72" data-id="element-1043" data-mp="cq1aqle8l8he">
            Project status, deliverables, invoices and a direct line to the team. Access is restricted to named
            users at your organisation.
          </p>
        </div>
        <p className="mt-16 text-small text-white/45 lg:mt-0" data-id="element-1044" data-mp="2z6kz71s7qr7">
          Personal data is held in the European Union. Every download is logged.
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-14 sm:px-12" data-id="element-1045" data-mp="3q7tv0b87zivx">
        <div className="w-full max-w-sm" data-id="element-1046" data-mp="3l64jyvkzz57u">
          {pendingMfa ? <form onSubmit={onMfa} className="space-y-6" data-id="element-1047" data-mp="3g7jfgjyppzdl">
              <div className="flex items-center gap-2 text-gold" data-id="element-1048" data-mp="3ga5m8hds4aur">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1049" data-mp="3c7iz2ts1ze3w" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1050" data-mp="1eh80t6mc0kju">Second step</span>
              </div>
              <div data-id="element-1051" data-mp="1ag5vq95qhe9y">
                <h2 className="font-display text-[26px] text-ink" data-id="element-1052" data-mp="1afw7aq0c7f6d">Enter your code</h2>
                <p className="mt-2 text-[15px] text-body" data-id="element-1053" data-mp="2sby9bf3r92c4">
                  Open your authenticator app and enter the six digit code for Cavalla Solution.
                </p>
              </div>
              <Field id="mfa-code" label="Authentication code" required error={error ?? undefined} data-id="element-1054" data-mp="323u86sdo0whe">
                <input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={event => setCode(event.target.value)} placeholder="204815" className={`${inputClass} tracking-[0.4em]`} data-id="element-1055" data-mp="362up25cl1p1r" />
              </Field>
              <Button type="submit" size="lg" disabled={busy} className="w-full" data-id="element-1056" data-mp="2nf7uev3oscsa">
                {busy ? 'Verifying' : 'Verify and continue'}
              </Button>
              <p className="text-small text-muted" data-id="element-1057" data-mp="2mmi0uv1s4bo">Demonstration code: 204815</p>
            </form> : <form onSubmit={onSignIn} className="space-y-6" data-id="element-1058" data-mp="17bds1cuojjd">
              <div data-id="element-1059" data-mp="3scjmrm8sxbt7">
                <h2 className="font-display text-[26px] text-ink" data-id="element-1060" data-mp="3sdctin7rg1bc">Client login</h2>
                <p className="mt-2 text-[15px] text-body" data-id="element-1061" data-mp="a6orx7bx5waf">
                  Sign in with the email address we set your account up against.
                </p>
              </div>
              <Field id="login-email" label="Email address" required data-id="element-1062" data-mp="67pmllm9zxig">
                <input id="login-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className={inputClass} data-id="element-1063" data-mp="k3jnpigt5il0" />
              </Field>
              <Field id="login-password" label="Password" required error={error ?? undefined} data-id="element-1064" data-mp="12vlxc9v17ylr">
                <input id="login-password" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} className={inputClass} data-id="element-1065" data-mp="xwj21h4ut6qz" />
              </Field>
              <Button type="submit" size="lg" disabled={busy} className="w-full" data-id="element-1066" data-mp="18h38alckkpb9">
                {busy ? 'Signing in' : 'Sign in'}
              </Button>

              <div className="flex flex-wrap items-center justify-between gap-3 text-[14px]" data-id="element-1067" data-mp="ys348nj7n3q7">
                <button type="button" onClick={() => setMagicSent(true)} className="text-royal underline underline-offset-4" data-id="element-1068" data-mp="13i9uertdewak">
                  Email me a sign in link
                </button>
                <Link to="/contact" className="text-royal underline underline-offset-4" data-id="element-1069" data-mp="tt049hi37np3">
                  Forgotten your password
                </Link>
              </div>
              {magicSent && <p role="status" className="rounded-sm bg-champagne p-3 text-[14px] text-body" data-id="element-1070" data-mp="ku4slpw9xlee">
                  If that address has an account, a single use sign in link is on its way. It expires in fifteen
                  minutes.
                </p>}

              <div className="rounded-sm border border-line bg-mist p-4 text-[13px] text-muted" data-id="element-1071" data-mp="2cs7mbdhv06eu">
                <p className="font-medium text-ink" data-id="element-1072" data-mp="28r478t7myeeo">Demonstration account</p>
                <p className="mt-1" data-id="element-1073" data-mp="35gbz0iyuljr1">a.mensah@northbridge.org</p>
                <p data-id="element-1074" data-mp="30gzbdw05k8ne">cavalla2026, then code 204815</p>
              </div>
            </form>}

          <p className="mt-8 text-small text-muted" data-id="element-1075" data-mp="2wfdrhqp7au3p">
            <Link to="/" className="underline underline-offset-4 hover:text-royal" data-id="element-1076" data-mp="2we2ldk8a9a9f">
              Return to the website
            </Link>
          </p>
        </div>
      </div>
    </div>;
}